import React, { useState, useEffect } from 'react';
import '../styles/social-media-ctas.css';

const SocialMediaCTAs = ({ variant = 'main' }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?category=social_media&public_only=true`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data.settings || {});
    } catch (err) {
      console.error('Error fetching social media settings:', err);
      setError('Failed to load social media links');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null; // Don't show anything while loading
  if (error) return null; // Don't show anything if there's an error

  const showTelegram = settings.social_media_show_telegram_cta && settings.social_media_telegram_url;
  const showFacebook = settings.social_media_show_facebook_cta && settings.social_media_facebook_url;

  // Don't render anything if no CTAs should be shown
  if (!showTelegram && !showFacebook) return null;

  if (variant === 'header') {
    return (
      <div className="social-media-ctas-header">
        {showTelegram && (
          <a
            href={settings.social_media_telegram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="header-cta-button telegram"
            title="Join our Telegram"
          >
            <svg viewBox="0 0 24 24" className="header-cta-icon">
              <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.256 1.297-1.374 7.266-1.934 9.638-.237.995-.698 1.322-1.147 1.354-.978.092-1.717-.642-2.663-1.256l-2.573-1.675c-1.215-.79-2.133-1.301-3.46-2.084-.887-.524-.304-1.259.187-1.8 1.276-1.409 2.98-2.81 4.012-3.853.418-.423.34-.644-.09-.644-.593 0-2.244 1.334-3.532 2.344-.938.734-1.854 1.084-2.737.66-.504-.242-.904-.556-1.176-.834-.272-.278-.412-.633-.412-1.058 0-.636.329-1.22.987-1.752l1.64-1.306c1.675-1.11 3.18-2.106 4.516-2.987.64-.423 1.34-.705 2.09-.843.75-.138 1.54-.113 2.36.077.82.19 1.66.528 2.52 1.012l1.32.745c.46.26.87.58 1.23.96s.66.81.9 1.29c.24.48.36 1.01.36 1.59 0 .42-.08.82-.24 1.19-.16.37-.4.7-.72.99z"/>
            </svg>
          </a>
        )}

        {showFacebook && (
          <a
            href={settings.social_media_facebook_url}
            target="_blank"
            rel="noopener noreferrer"
            className="header-cta-button facebook"
            title="Follow us on Facebook"
          >
            <svg viewBox="0 0 24 24" className="header-cta-icon">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={`social-media-ctas ${variant === 'mobile' ? 'mobile-variant' : ''}`}>
      <div className="ctas-container">
        {variant !== 'mobile' && <h3 className="ctas-title">Connect With Us</h3>}
        <div className="ctas-buttons">
          {showTelegram && (
            <a
              href={settings.social_media_telegram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button telegram-cta"
            >
              <div className="cta-icon">
                <svg viewBox="0 0 24 24" className="telegram-icon">
                  <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.256 1.297-1.374 7.266-1.934 9.638-.237.995-.698 1.322-1.147 1.354-.978.092-1.717-.642-2.663-1.256l-2.573-1.675c-1.215-.79-2.133-1.301-3.46-2.084-.887-.524-.304-1.259.187-1.8 1.276-1.409 2.98-2.81 4.012-3.853.418-.423.34-.644-.09-.644-.593 0-2.244 1.334-3.532 2.344-.938.734-1.854 1.084-2.737.66-.504-.242-.904-.556-1.176-.834-.272-.278-.412-.633-.412-1.058 0-.636.329-1.22.987-1.752l1.64-1.306c1.675-1.11 3.18-2.106 4.516-2.987.64-.423 1.34-.705 2.09-.843.75-.138 1.54-.113 2.36.077.82.19 1.66.528 2.52 1.012l1.32.745c.46.26.87.58 1.23.96s.66.81.9 1.29c.24.48.36 1.01.36 1.59 0 .42-.08.82-.24 1.19-.16.37-.4.7-.72.99z"/>
                </svg>
              </div>
              <div className="cta-text">
                <span className="cta-label">Find Us On</span>
                <span className="cta-platform">Telegram</span>
              </div>
              <div className="cta-arrow">→</div>
            </a>
          )}

          {showFacebook && (
            <a
              href={settings.social_media_facebook_url}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button facebook-cta"
            >
              <div className="cta-icon">
                <svg viewBox="0 0 24 24" className="facebook-icon">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <div className="cta-text">
                <span className="cta-label">Find Us On</span>
                <span className="cta-platform">Facebook</span>
              </div>
              <div className="cta-arrow">→</div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaCTAs;